import moment from "moment";
import { useMemo, useState } from "react";
import CommonFreeTextInput from "../../common/inputs/CommonFreeTextInput";

export default function ExpiryInput({ callback }) {
  const [data, setData] = useState({
    date: moment().add(3, "hours"),
    day: 0,
    hour: 3,
    min: 0,
  });

  const clamp = (n, min, max) => Math.min(Math.max(n, min), max);

  // ✅ numbers-only: ignore non-numeric completely
  const toIntStrict = (v) => {
    if (typeof v === "number") return Number.isFinite(v) ? Math.floor(v) : null;
    if (typeof v !== "string") return null;
    const s = v.trim();
    if (!/^\d+$/.test(s)) return null;
    return parseInt(s, 10);
  };

  function handleChange(obj) {
    const nextDayRaw = toIntStrict(obj?.day);
    const nextHourRaw = toIntStrict(obj?.hour);
    const nextMinRaw = toIntStrict(obj?.min);

    // ignore non-numeric input
    const day = nextDayRaw === null ? data.day : clamp(nextDayRaw, 0, 14);
    const hour = nextHourRaw === null ? data.hour : clamp(nextHourRaw, 0, 23);
    const min = nextMinRaw === null ? data.min : clamp(nextMinRaw, 0, 59);

    const newData = {
      ...data,
      ...obj,
      day,
      hour,
      min,
      date: moment().add(day, "days").add(hour, "hours").add(min, "minutes"),
    };

    setData(newData);
    callback && callback(newData?.date);
  }

  // Tue, Oct 21 14:05
  const formatted = useMemo(
    () => moment(data?.date).format("ddd, MMM D HH:mm"),
    [data?.date]
  );

  return (
    <div className="w-full rounded-2xl border border-gray-200 bg-white p-4">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="flex flex-col">
          <div className="text-sm font-semibold text-gray-800">
            Payment Expiry
          </div>
          <div className="text-xs text-gray-500">
            Set how long this quote stays valid.
          </div>
        </div>

        <div className="flex flex-col items-start sm:items-end">
          <div className="text-xs text-gray-500">Expires at</div>
          <div className="rounded-full bg-red-50 px-3 py-1 text-sm font-semibold text-red-600">
            {formatted}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="mt-4 grid grid-cols-1 gap-3 rounded-2xl border border-gray-100 bg-gray-50 p-3 sm:grid-cols-3">
        {/* Days */}
        <div className="rounded-xl bg-white border border-gray-200 p-3">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-xs font-semibold text-gray-700">Days</div>
            <div className="text-[10px] font-bold text-gray-400">+</div>
          </div>

          <CommonFreeTextInput
            label=""
            placeholder="0"
            inputMode="numeric"
            value={String(data?.day ?? "")}
            onChange={(v) => handleChange({ day: v })}
            className="!w-full"
          />
          <div className="mt-2 text-[11px] text-gray-500">0–14</div>
        </div>

        {/* Hours */}
        <div className="rounded-xl bg-white border border-gray-200 p-3">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-xs font-semibold text-gray-700">Hours</div>
            <div className="text-[10px] font-bold text-gray-400">+</div>
          </div>

          <CommonFreeTextInput
            label=""
            placeholder="0"
            inputMode="numeric"
            value={String(data?.hour ?? "")}
            onChange={(v) => handleChange({ hour: v })}
            className="!w-full"
          />
          <div className="mt-2 text-[11px] text-gray-500">0–23</div>
        </div>

        {/* Minutes */}
        <div className="rounded-xl bg-white border border-gray-200 p-3">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-xs font-semibold text-gray-700">Minutes</div>
            <div className="text-[10px] font-bold text-gray-400">+</div>
          </div>

          <CommonFreeTextInput
            label=""
            placeholder="0"
            inputMode="numeric"
            value={String(data?.min ?? "")}
            onChange={(v) => handleChange({ min: v })}
            className="!w-full"
          />
          <div className="mt-2 text-[11px] text-gray-500">0–59</div>
        </div>
      </div>

      {/* Quick presets */}
      <div className="mt-4 flex flex-wrap gap-2">
        {[
          { label: "30 min", day: 0, hour: 0, min: 30 },
          { label: "1 hour", day: 0, hour: 1, min: 0 },
          { label: "3 hours", day: 0, hour: 3, min: 0 },
          { label: "1 day", day: 1, hour: 0, min: 0 },
        ].map((p) => (
          <button
            key={p.label}
            type="button"
            onClick={() => handleChange(p)}
            className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50"
          >
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );
}
